import * as React from 'react';
import { NavLink } from 'react-router';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ColorModeIconDropdown from './theme/ColorModeIconDropdown';
import Sitemark from './sitemark-icon';
import { withRouter } from './with-router';
import Menu from '@mui/material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

// Fixed width transition
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: theme.spacing(1),
  transition: theme.transitions.create('width'),
  width: 150, // Fixed initial width
  border: '1px solid',
  borderColor: theme.palette.divider,
  [theme.breakpoints.up('sm')]: {
    width: 250, // Same for larger screens
    '&:focus-within': {
      width: 300, // Expanded width when focused
    },
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Darker icon color
  color: theme.palette.text.primary,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.palette.text.primary, // Darker text color
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0), // Right padding for end adornment
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
}));

// Define router props interface
interface RouterProps {
  location: any;
  navigate: any;
  params: any;
}

interface NavBarState {
  open: boolean;
  searchModel: 'compounds' | 'complexes';
  searchQuery: string;
  browseAnchorEl: HTMLElement | null;
}

interface NavBarProps extends RouterProps {}

class NavBar extends React.Component<NavBarProps, NavBarState> {
  constructor(props: NavBarProps) {
    super(props);
    this.state = {
      open: false,
      searchModel: 'compounds',
      searchQuery: '',
      browseAnchorEl: null,
    };
  }

  toggleDrawer = (newOpen: boolean) => {
    this.setState({ open: newOpen });
  };

  toggleSearchModel = () => {
    this.setState(prevState => ({
      searchModel: prevState.searchModel === 'compounds' ? 'complexes' : 'compounds',
      searchQuery: '', // Clear search when toggling model
    }));
  };

  handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchQuery: event.target.value });
  };

  handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.handleSearch();
    }
  };

  handleSearch = () => {
    const { searchModel, searchQuery } = this.state;
    const query = searchQuery.trim();
    
    if (query) {
      // Navigate to the search page with the query parameters
      this.props.navigate(`/search?model=${searchModel}&q=All:${encodeURIComponent(query)}&page=1`);
      this.setState({ searchQuery: '' }); // Clear search after navigation
    }
  };

  // Handle Browse menu open
  handleBrowseMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ browseAnchorEl: event.currentTarget });
  };

  // Handle Browse menu close
  handleBrowseMenuClose = () => {
    this.setState({ browseAnchorEl: null });
  };

  render() {
    const { open, searchModel, searchQuery, browseAnchorEl } = this.state;
    const placeholder = `Search ${searchModel === 'compounds' ? 'compounds' : 'complexes'}`;
    const ToggleIcon = searchModel === 'compounds' ? ToggleOffIcon : ToggleOnIcon;

    return (
      <AppBar
        position="fixed"
        enableColorOnDark
        sx={{
          boxShadow: 0,
          bgcolor: 'transparent',
          backgroundImage: 'none',
          mt: 'calc(var(--template-frame-height, 0px) + 28px)',
        }}
      >
        <Container maxWidth="lg">
          <StyledToolbar variant="dense" disableGutters>
            {/* Left section: Logo and navigation links */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Typography component={NavLink} to="/" sx={{ display: 'flex', alignItems: 'center' }}>
                <Sitemark />
              </Typography>
              
              <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 2 }}>
                <Button 
                  component={NavLink} 
                  to='/' 
                  variant="text" 
                  color="info" 
                  size="small"
                >
                  Home
                </Button>
                {/* Browse menu with dropdown */}
                <Button 
                  variant="text" 
                  color="info" 
                  size="small"
                  onClick={this.handleBrowseMenuOpen}
                  endIcon={<ArrowDropDownIcon />}
                >
                  Browse
                </Button>
                <Button component={NavLink} to='/download' variant="text" color="info" size="small">
                  Download
                </Button>
                <Button component={NavLink} to='/about' variant="text" color="info" size="small">
                  About
                </Button>
                <Button component={NavLink} to='/statistics' variant="text" color="info" size="small">
                  Statistics
                </Button>
                <Button component={NavLink} to='/help' variant="text" color="info" size="small">
                  Help
                </Button>
                <Button component={NavLink} to='/contact' variant="text" color="info" size="small">
                  Contact
                </Button>
                <Button component={NavLink} to='/timeline' variant="text" color="info" size="small">
                  Timeline
                </Button>
              </Box>
            </Box>
            
            {/* Right section: Search and color mode */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexGrow: 0,
            }}>
              {/* Search Container */}
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                mr: 1,
              }}>
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder={placeholder}
                    inputProps={{ 'aria-label': 'search' }}
                    value={searchQuery}
                    onChange={this.handleSearchQueryChange}
                    onKeyPress={this.handleKeyPress}
                    // Add endAdornment for model toggle
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={this.toggleSearchModel}
                          sx={{ 
                            color: 'inherit',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                          }}
                        >
                          <ToggleIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </Search>
              </Box>
              
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <ColorModeIconDropdown />
              </Box>
            </Box>
            
            {/* Mobile menu */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
              <ColorModeIconDropdown size="medium" />
              <IconButton aria-label="Menu button" onClick={() => this.toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="top"
                open={open}
                onClose={() => this.toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    top: 'var(--template-frame-height, 0px)',
                  },
                }}
              >
                <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <IconButton onClick={() => this.toggleDrawer(false)}>
                      <CloseRoundedIcon />
                    </IconButton>
                  </Box>

                  <MenuItem component={NavLink} to='/'>Home</MenuItem>
                  <MenuItem component={NavLink} to='/browse/compounds'>Compounds</MenuItem>
                  <MenuItem component={NavLink} to='/browse/complexes'>Complexes</MenuItem>
                  <MenuItem component={NavLink} to='/browse/citations'>Citations</MenuItem>
                  <MenuItem component={NavLink} to='/download'>Download</MenuItem>
                  <MenuItem component={NavLink} to='/about'>About</MenuItem>
                  <MenuItem component={NavLink} to='/statistics'>Statistics</MenuItem>
                  <MenuItem component={NavLink} to='/help'>Help</MenuItem>
                  <MenuItem component={NavLink} to='/contact'>Contact</MenuItem>
                  <MenuItem component={NavLink} to='/timeline'>Timeline</MenuItem>
                </Box>
              </Drawer>
            </Box>
          </StyledToolbar>
        </Container>
        
        {/* Browse dropdown menu for desktop */}
        <Menu
          anchorEl={browseAnchorEl}
          open={Boolean(browseAnchorEl)}
          onClose={this.handleBrowseMenuClose}
          MenuListProps={{
            'aria-labelledby': 'browse-menu',
          }}
        >
          <MenuItem 
            component={NavLink} 
            to="/browse/compounds" 
            onClick={this.handleBrowseMenuClose}
          >
            Compounds
          </MenuItem>
          <MenuItem 
            component={NavLink} 
            to="/browse/complexes" 
            onClick={this.handleBrowseMenuClose}
          >
            Complexes
          </MenuItem>
          <MenuItem 
            component={NavLink} 
            to="/browse/citations" 
            onClick={this.handleBrowseMenuClose}
          >
            Citations
          </MenuItem>
        </Menu>
      </AppBar>
    );
  }
}

export default withRouter(NavBar);